import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Boxes,
  Wrench,
  Search,
  FileText,
  Megaphone,
  Settings,
  ScrollText,
  LogOut,
  Zap,
  Menu,
  BookOpen,
  Tags,
  House,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useI18n } from "@/i18n";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/admin", exact: true, icon: LayoutDashboard, key: "dashboard" },
  { to: "/admin/platforms", icon: Boxes, key: "platforms" },
  { to: "/admin/tools", icon: Wrench, key: "tools" },
  { to: "/admin/seo", icon: Search, key: "seo_content" },
  { to: "/admin/content", icon: FileText, key: "content" },
  { to: "/admin/articles", icon: BookOpen, key: "articles" },
  { to: "/admin/article-categories", icon: Tags, key: "article_categories" },
  { to: "/admin/home-content", icon: House, key: "home_content" },
  { to: "/admin/ads", icon: Megaphone, key: "ads" },
  { to: "/admin/settings", icon: Settings, key: "settings" },
  { to: "/admin/logs", icon: ScrollText, key: "logs" },
];

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const SidebarInner = (
    <>
      <div className="flex items-center gap-2 px-5 h-16 border-b border-border">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground">
          <Zap className="w-4 h-4" fill="currentColor" />
        </span>
        <span className="font-head font-extrabold">ARAN <span className="text-primary">Admin</span></span>
      </div>
      <nav aria-label="القائمة الإدارية" className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.key}
            to={n.to}
            end={n.exact}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.key}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`
            }
          >
            <n.icon className="w-4 h-4" />
            {t(n.key)}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="px-2 pb-2 text-xs text-muted-foreground truncate">{admin?.email}</div>
        <button
          type="button"
          onClick={handleLogout}
          data-testid="admin-logout-button"
          className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {t("logout")}
        </button>
      </div>
    </>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
    <div className="min-h-screen bg-background flex" dir="rtl">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-l border-border bg-card/40 sticky top-0 h-screen">
        {SidebarInner}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden glass-header h-14 flex items-center justify-between px-4">
          <SheetTrigger asChild>
            <button type="button" className="p-2 rounded-lg hover:bg-secondary" aria-label="فتح القائمة الإدارية">
              <Menu className="w-6 h-6" aria-hidden="true" />
            </button>
          </SheetTrigger>
          <span className="font-head font-bold">ARAN Admin</span>
          <span className="w-9" />
        </header>
        <SheetContent side="right" closeLabel="إغلاق القائمة الإدارية" className="flex w-64 max-w-[85vw] flex-col border-border bg-card p-0">
          <SheetTitle className="sr-only">القائمة الإدارية</SheetTitle>
          <div className="h-12 shrink-0" aria-hidden="true" />
          {SidebarInner}
        </SheetContent>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-6xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
    </Sheet>
  );
}
